import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, ChevronRight, Clock, Scale } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, ConfidenceBar } from "@hp/ui";
import { fetchWatchNotes } from "@hp/api-client";
import type { ClaimCandidateStatus } from "@hp/types";
import { fetchTrueupSummary } from "../api/trueup";
import { fetchPipelineSummary } from "../api/pipeline";
import { fetchCompanyAgreements } from "../api/companyAgreements";
import { StatTile } from "../components/StatTile";
import { TopReviewersCard } from "../components/TopReviewersCard";
import { ErrorBlock, LoadingBlock } from "../components/QueryStatus";
import { formatCount, formatDate, formatIssueTag, formatForum, formatINR } from "../lib/format";
import { OVERVIEW_ONLY } from "../lib/demoMode";
import { getAlertSettings } from "../api/alertSettings";
import { ClaimsModal } from "../components/ClaimsModal";
import { PageHeader } from "../components/layout/PageHeader";
import { CilEventsOverview } from "./reports/CilChangeEventsReportPage";
import { WatchNotesList, watchNoteCounts, splitWatchNotes } from "../components/WatchNoteCard";
import { ErrorState, LoadingState } from "../components/Feedback";
import { USE_MOCK_DATA } from "../api/config";
import { simulateNetwork } from "../api/simulateNetwork";
import {
  getPortfolioQuantificationStatus,
  hasPortfolioClaimActivity,
  portfolioDisplayAmounts,
} from "../lib/portfolioScanStatus";

/** Freshly surfaced, not yet reviewed - the top of the claims funnel. */
/** Reviewed and being actively pursued, short of recovered. */
const IN_PROGRESS_STATUSES: ClaimCandidateStatus[] = ["validated", "filed", "allowed"];

const PROCESSING_BADGES: Record<string, { label: string; tone: "warning" | "info" | "success" | "neutral" }> = {
  needs_ocr: { label: "Needs OCR", tone: "warning" },
  extracted: { label: "Clauses only", tone: "info" },
  ledger_ready: { label: "Ledger ready", tone: "success" },
  placeholder: { label: "Placeholder", tone: "neutral" },
};

const PIPELINE_STATUS_BADGES: Record<string, { label: string; tone: "success" | "brand" | "warning" | "neutral" }> = {
  open: { label: "In pipeline", tone: "brand" },
  recovered: { label: "Recovered", tone: "success" },
};

/** The single screen a non-technical exec opens first: the state of the whole claims portfolio, at a glance. */
/** The "Portfolio scan" contracts table is hidden on the home page for now
 * (and its data is not fetched); flip to bring it back. */
const SHOW_PORTFOLIO_SCAN = false;

/**
 * "Watch notes": other parties' rulings and notices that may matter to HP.
 * A separate section on purpose - a watch note is not a change event (nothing
 * HP was allowed or refused changed), so it is never added to the opportunity
 * or risk numbers, and the "hide events without an HP order" setting, which is
 * about change events, does not filter it.
 */
export function WatchNotesSection() {
  // One request for both lists: the cards and the "checked, nothing found" log.
  const query = useQuery({
    queryKey: ["cil", "watch-notes", { includeNoMatch: true }],
    queryFn: () => (USE_MOCK_DATA ? simulateNetwork([]) : fetchWatchNotes({ includeNoMatch: true })),
    refetchInterval: 10_000, // like the change events above: a note appears as its file finishes reading
  });
  const counts = query.data ? watchNoteCounts(splitWatchNotes(query.data).cards) : null;
  return (
    <Card aria-labelledby="watch-notes-title">
      <CardHeader>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <CardTitle id="watch-notes-title">Watch notes: other parties' rulings and notices</CardTitle>
          {counts ? (
            <span className="text-xs tabular-nums text-zinc-500">
              {counts.act_now} act now · {counts.watch} watch · {counts.no_action} no action
            </span>
          ) : null}
        </div>
        <p className="text-xs text-zinc-500">
          Not counted in the opportunity or risk numbers above: nothing HP was allowed or refused changed, but another
          party's ruling or a notice may matter.
        </p>
      </CardHeader>
      <CardBody>
        {query.isPending ? <LoadingState label="Loading watch notes..." /> : null}
        {query.isError ? <ErrorState error={query.error} label="Could not load watch notes" /> : null}
        {query.data ? <WatchNotesList notes={query.data} /> : null}
      </CardBody>
    </Card>
  );
}


export function OverviewPage() {
  const [showAllContracts, setShowAllContracts] = React.useState(false);
  const [claimsModalPath, setClaimsModalPath] = React.useState<string | null>(null);
  const closeClaimsModal = React.useCallback(() => setClaimsModalPath(null), []);
  const pipelineQuery = useQuery({ queryKey: ["dashboard", "pipeline-summary"], queryFn: fetchPipelineSummary });
  const contractsQuery = useQuery({ queryKey: ["company-agreements"], queryFn: fetchCompanyAgreements, enabled: SHOW_PORTFOLIO_SCAN });
  // Org-wide setting (Settings > Dashboard home): whether the "True-up gaps" card is shown.
  const settingsQuery = useQuery({ queryKey: ["alert-settings"], queryFn: () => getAlertSettings() });
  const showTrueUpGaps = settingsQuery.data?.show_true_up_gaps === true;
  const showTopReviewers = settingsQuery.data?.show_top_reviewers !== false;
  const trueupQuery = useQuery({ queryKey: ["trueup", "summary"], queryFn: fetchTrueupSummary, enabled: showTrueUpGaps });
  const trueup = trueupQuery.data;

  const pipeline = pipelineQuery.data;
  const contracts = contractsQuery.data;

  const inProgressBuckets = pipeline?.by_status.filter((s) => IN_PROGRESS_STATUSES.includes(s.status)) ?? [];
  const inProgressCount = inProgressBuckets.reduce((sum, bucket) => sum + bucket.count, 0);
  const inProgressValueHigh = inProgressBuckets.reduce((sum, bucket) => sum + bucket.value_high_total, 0);

  const sortedContracts = contracts
    ? [...contracts].sort((a, b) => {
        const rank = (row: typeof a) =>
          row.portfolio_pipeline_status === "open"
            ? 0
            : row.portfolio_pipeline_status === "recovered"
              ? 1
              : 2;
        return (
          rank(a) - rank(b) ||
          b.flagged_claim_value_high_total - a.flagged_claim_value_high_total ||
          b.recovered_claim_value_high_total - a.recovered_claim_value_high_total
        );
      })
    : [];
  const contractsWithActivity = sortedContracts.filter(hasPortfolioClaimActivity);
  const contractsWithoutActivity = sortedContracts.filter((row) => !hasPortfolioClaimActivity(row));
  const visibleContracts = showAllContracts ? sortedContracts : contractsWithActivity;

  const showTiles = showTopReviewers || showTrueUpGaps || !OVERVIEW_ONLY;
  return (
    <div className="page-stack">
      <PageHeader title="Overview" description="Every changed event, by what a reviewer has done with it. Click a row to open its rulings, justification and actions." />
      <CilEventsOverview />
      <WatchNotesSection />
      {showTiles ? (
      <div
        className={[
          "grid grid-cols-1 gap-4 sm:grid-cols-2",
          "lg:grid-cols-3",
        ].join(" ")}
      >
        {showTopReviewers ? <TopReviewersCard /> : null}
        {showTrueUpGaps ? (
        <StatTile
          label="True-up gaps"
          value={trueup ? formatCount(trueup.material_gaps) : "…"}
          sublabel={
            trueup && trueup.material_gaps
              ? `HP short by Rs ${trueup.total_gap_cr.toLocaleString("en-IN", { maximumFractionDigits: 2 })} Cr across ${trueup.orders} true-up orders`
              : "claimed vs allowed in MPERC true-up orders"
          }
          icon={Scale}
          tone="critical"
          onClick={() => setClaimsModalPath("/popup/trueup-gaps")}
          actionLabel="View gaps"
        />
        ) : null}
        {OVERVIEW_ONLY ? null : (
          <StatTile
            label="In progress claims"
            value={pipeline ? formatINR(inProgressValueHigh) : "…"}
            sublabel={`${formatCount(inProgressCount)} validated, filed, or allowed`}
            icon={Clock}
            tone="warning"
          />
        )}
      </div>
      ) : null}

      {SHOW_PORTFOLIO_SCAN ? (
        <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-zinc-400" aria-hidden />
            <CardTitle className="text-zinc-900">Portfolio scan</CardTitle>
          </div>
          <p className="mt-1 text-xs text-zinc-500">
            Contracts with open or recovered claim candidates. Status shows where each contract sits in the
            pipeline.
          </p>
          {contractsWithoutActivity.length > 0 ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setShowAllContracts((current) => !current)}
              >
                {showAllContracts
                  ? "Hide unquantified contracts"
                  : `Show all contracts (+${contractsWithoutActivity.length} without claims)`}
              </Button>
              <span className="text-xs text-zinc-500">
                {showAllContracts
                  ? "Contracts without claims show why amounts are not available yet."
                  : "Only contracts with open or recovered claims are shown by default."}
              </span>
            </div>
          ) : null}
        </CardHeader>
        <CardBody className="p-0">
          {contractsQuery.isPending ? <LoadingBlock label="Loading contracts" /> : null}
          {contractsQuery.isError ? <ErrorBlock message="Could not load HP contracts." /> : null}
          {contracts ? (
            <div className="table-scroll">
              {visibleContracts.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-zinc-500 sm:px-5">
                  No contracts with priced claims yet.{" "}
                  {contractsWithoutActivity.length > 0 ? (
                    <button
                      type="button"
                      className="font-medium text-brand-700 hover:underline"
                      onClick={() => setShowAllContracts(true)}
                    >
                      Show {contractsWithoutActivity.length} other contract
                      {contractsWithoutActivity.length === 1 ? "" : "s"}
                    </button>
                  ) : null}
                </p>
              ) : (
                <table className="table-data text-left text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200">
                      <th className="min-w-[12rem] font-medium">Instrument</th>
                      <th className="col-hide-md min-w-[10rem] font-medium">
                        {OVERVIEW_ONLY ? "Regulator" : "Regulator / model"}
                      </th>
                      <th className="col-hide-lg min-w-[6rem] font-medium">Effective</th>
                      <th className="min-w-[5.5rem] font-medium">Status</th>
                      <th className="min-w-[4rem] font-medium">Claims</th>
                      <th className="col-hide-md min-w-[7rem] font-medium">Confidence</th>
                      <th className="min-w-[5.5rem] font-medium">Principal</th>
                      <th className="col-hide-lg min-w-[5rem] font-medium">LPS</th>
                      <th className="min-w-[5.5rem] font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleContracts.map((agreement) => {
                      const processingBadge = PROCESSING_BADGES[agreement.processing_status];
                      const quantification = getPortfolioQuantificationStatus(agreement);
                      const amounts = portfolioDisplayAmounts(agreement);
                      const pipelineBadge = amounts
                        ? PIPELINE_STATUS_BADGES[amounts.pipelineStatus]
                        : null;
                      const rowTone =
                        amounts?.pipelineStatus === "open"
                          ? "border-b border-zinc-200 bg-emerald-50/40 last:border-0"
                          : amounts?.pipelineStatus === "recovered"
                            ? "border-b border-zinc-200 bg-sky-50/40 last:border-0"
                            : "border-b border-zinc-100 bg-zinc-50/30 last:border-0";

                      return (
                        <tr key={agreement.id} className={rowTone}>
                          <td>
                            <Link
                              to={`/portfolio/${agreement.id}`}
                              className="group inline-flex max-w-[18rem] items-start gap-2 text-left hover:text-brand-700 sm:max-w-none"
                            >
                              <div className="min-w-0">
                                <div className="font-medium text-zinc-900 group-hover:text-brand-700">
                                  {agreement.name}
                                </div>
                                <div className="text-xs text-zinc-500">{agreement.counterparty}</div>
                                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                  {processingBadge ? <Badge tone={processingBadge.tone}>{processingBadge.label}</Badge> : null}
                                  <span className="text-[0.6875rem] text-zinc-400 md:hidden">
                                    {OVERVIEW_ONLY
                                      ? formatForum(agreement.forum)
                                      : `${formatForum(agreement.forum)} · ${formatIssueTag(agreement.agreement_type)}`}
                                  </span>
                                </div>
                              </div>
                              <ChevronRight
                                className="mt-0.5 h-4 w-4 flex-none text-zinc-300 group-hover:text-brand-500"
                                aria-hidden
                              />
                            </Link>
                          </td>
                          <td className="col-hide-md text-zinc-600">
                            {OVERVIEW_ONLY
                              ? formatForum(agreement.forum)
                              : `${formatForum(agreement.forum)} · ${formatIssueTag(agreement.agreement_type)}`}
                          </td>
                          <td className="col-hide-lg text-zinc-600">{formatDate(agreement.effective_date)}</td>
                          {amounts ? (
                            <>
                              <td>
                                {pipelineBadge ? <Badge tone={pipelineBadge.tone}>{pipelineBadge.label}</Badge> : null}
                              </td>
                              <td className="text-zinc-600">
                                {formatCount(amounts.claimCount)}
                                {amounts.pipelineStatus === "recovered" ? (
                                  <span className="block text-[0.6875rem] text-zinc-400">recovered group(s)</span>
                                ) : null}
                              </td>
                              <td className="col-hide-md">
                                {amounts.confidence != null ? (
                                  <ConfidenceBar value={Math.round(amounts.confidence * 100)} className="min-w-[5rem]" />
                                ) : (
                                  <span className="text-zinc-400">—</span>
                                )}
                              </td>
                              <td className="font-mono tabular-nums text-zinc-900">
                                {formatINR(amounts.principal)}
                              </td>
                              <td className="col-hide-lg font-mono tabular-nums text-zinc-600">
                                {amounts.carrying > 0 ? formatINR(amounts.carrying) : "—"}
                              </td>
                              <td className="font-mono tabular-nums font-medium text-zinc-900">
                                {formatINR(amounts.total)}
                              </td>
                            </>
                          ) : (
                            <>
                              <td>
                                <Badge tone="neutral">Not quantified</Badge>
                              </td>
                              <td
                                className="text-zinc-500"
                                colSpan={5}
                                title={quantification?.detail}
                              >
                                <span className="text-sm font-medium text-zinc-600">
                                  {quantification?.shortLabel}
                                </span>
                                <span className="mt-0.5 block text-xs text-zinc-400">
                                  {quantification?.detail} Open the contract for next steps.
                                </span>
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          ) : null}
        </CardBody>
      </Card>
      ) : null}

      {claimsModalPath ? <ClaimsModal path={claimsModalPath} onClose={closeClaimsModal} /> : null}
    </div>
  );
}
